import type { i18n as I18nInstance } from "i18next"

/**
 * OKS-1023 — keep `<html lang>` equal to the language the page's text is
 * rendered in (WCAG 3.1.1).
 *
 * The server paints the first value from `I18n.locale` in
 * `app/views/layouts/application.html.erb`. On the client, the text language
 * is decided by i18next on every path that can change it, so the attribute is
 * bound to i18next itself rather than to any one prop:
 *   - signed-in pages and the no-reload settings switch → the root `locale`
 *     prop → `syncI18nLocale` on router `success`;
 *   - anonymous public pages → `initI18n({ locale: resolvePublicLocale(…) })`,
 *     so a `?lang=` / `?locale=` override wins over the server's brand-locale
 *     paint.
 *
 * Not a React hook on purpose: a hook needs a mount point, and pages that set
 * their own `layout` skip `PersistentLayout`. React never owns `<html>` (the
 * root is `#app`), so there is no hydration conflict over the attribute.
 *
 * Binds each instance at most once (`initI18n` runs on every public-page
 * render). No-op where there is no `document` (the SSR sidecar).
 */
const boundInstances = new WeakSet<I18nInstance>()

function applyLang(instance: I18nInstance): void {
  const lang = instance.resolvedLanguage ?? instance.language
  if (lang) document.documentElement.lang = lang
}

export function bindDocumentLang(instance: I18nInstance): void {
  if (typeof document === "undefined") return
  if (boundInstances.has(instance)) return
  boundInstances.add(instance)
  instance.on("languageChanged", () => applyLang(instance))
  applyLang(instance)
}
